import React from "react";
import { motion } from "framer-motion";
import {
  Users,
  Target,
  Award,
  Heart,
  CheckCircle,
  ArrowRight,
  Flag,
  Globe,
  Shield,
  DollarSign,
  ShoppingBag,
  Sparkles,
  ShieldCheck,
  Zap,
} from "lucide-react";
import { Link } from "react-router-dom";
import ceoImage from "../components/assets/about-image-3-CfFyGLWa.jpg";
import CommonContactUs from "./CommonContactUs";

const AboutPage = () => {
  const values = [
    {
      icon: Sparkles,
      title: "Excellence",
      description:
        "We believe in the power of teamwork and partnership to achieve exceptional results and drive innovation.",
    },
    {
      icon: Users,
      title: "Collaboration",
      description:
        "We believe in the power of teamwork and building strong partnerships with our clients.",
    },
    {
      icon: Zap,
      title: "Integrity",
      description:
        "We conduct business with the highest ethical standards, building trust through transparency and honesty.",
    },
    {
      icon: ShieldCheck,
      title: "Innovation",
      description:
        "We embrace change and continuously seek new ways to solve complex problems and create value for our clients.",
    },
  ];

  const timelineData = [
    {
      year: "2010",
      title: "The Beginning",
      description:
        "Founded with a mission to connect tech talent with innovative companies. Our journey began with a small team of passionate technologists in a modest office.",
      icon: Flag,
      link: "Our first milestone",
      isActive: false,
    },
    {
      year: "2015",
      title: "Global Expansion",
      description:
        "Expanded operations to serve clients across 15 countries, establishing regional offices and building diverse, multicultural teams.",
      icon: Globe,
      link: "Our global footprint",
      isActive: true,
    },
    {
      year: "2020",
      title: "Digital Transformation",
      description:
        "Launched innovative solutions to help clients navigate the challenges of a rapidly changing digital landscape, bringing cutting-edge technologies to traditional industries.",
      icon: Shield,
      link: "Our innovation hub",
      isActive: true,
    },
    {
      year: "Today",
      title: "Industry Leader",
      description:
        "Recognized as a leader in providing tech talent and innovative solutions to businesses worldwide, with a community of over 5,000 skilled professionals.",
      icon: Shield,
      link: "Our leadership journey",
      isActive: true,
    },
  ];

  const team = [
    {
      name: "David Chen",
      role: "CEO & Founder",
      image: "/assets/team.jpg",
      bio: "Former tech executive with 15+ years of experience in HR technology and digital transformation.",
    },
    {
      name: "Sarah Williams",
      role: "CTO",
      image: "/assets/team.jpg",
      bio: "Technology leader with expertise in AI, machine learning, and scalable software architecture.",
    },
    {
      name: "Michael Rodriguez",
      role: "Head of Operations",
      image: "/assets/team.jpg",
      bio: "Operations expert with deep knowledge in process optimization and client success management.",
    },
    {
      name: "Emily Johnson",
      role: "VP of Talent",
      image: "/assets/team.jpg",
      bio: "HR professional with 12+ years of experience in talent acquisition and organizational development.",
    },
  ];

  const successStories = [
    {
      icon: DollarSign,
      title: "Financial Services Transformation",
      description:
        "Helped a leading bank reduce operational costs by 30% through digital transformation and process automation.",
      results: "30% cost reduction, 45% faster processing",
    },
    {
      icon: Heart,
      title: "Healthcare Innovation",
      description:
        "Developed a patient management system that improved care coordination and reduced administrative burden.",
      results: "40% improved patient satisfaction",
    },
    {
      icon: ShoppingBag,
      title: "Retail Tech Implementation",
      description:
        "Implemented an omnichannel retail solution that integrated online and offline operations for a national retailer.",
      results: "65% increase in online sales",
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#1A193B] pt-20 text-white">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-3 lg:px-4 py-24 lg:py-32">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              About TechMind Infosys
            </h1>
            <span class="text-lg md:text-xl lg:text-2xl  text-white font-medium">
              Pioneering innovation through technology and talent
            </span>
            <p class="mb-6 backdrop-blur-sm bg-gray-900/80 py-4 px-10 rounded-lg text-gray-100">
              Founded in 2010, TechMind Infosys has been at the forefront of
              digital transformation, helping businesses across the globe
              harness the power of technology to drive growth and innovation.
            </p>
            <Link to='/services'>
            <button
              class="px-8 py-3 bg-white text-blue-600 font-medium rounded-lg hover:bg-blue-50 shadow-lg transition-all"
              tabindex="0"
            >
              Discover Our Solutions
            </button>
            </Link>
            {/* <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Founded in 2010, TechMind Infosys has been at the forefront of
              digital transformation, helping businesses across the globe
              harness the power of technology to drive growth and innovation.
            </p> */}
          </motion.div>
        </div>
      </section>

      {/* Mission & Vision */}
      {/* <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                To empower businesses with innovative technology solutions that streamline HR processes, 
                enhance talent acquisition, and drive organizational success in the digital age.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We believe that every organization deserves access to cutting-edge HR technology that 
                not only simplifies operations but also creates meaningful connections between employers and talent.
              </p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-gray-50 p-8 rounded-xl"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-4">Our Vision</h3>
              <p className="text-gray-600 mb-6">
                To be the global leader in HR technology innovation, creating a world where every 
                business can easily find, hire, and retain the perfect talent for their needs.
              </p>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Innovation First</h4>
                    <p className="text-gray-600 text-sm">Leading the way in HR technology advancement</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Client Success</h4>
                    <p className="text-gray-600 text-sm">Dedicated to achieving measurable results</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-blue-600 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-900">Global Impact</h4>
                    <p className="text-gray-600 text-sm">Transforming businesses worldwide</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section> */}

      {/* Timeline Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
              Our Origin Story
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              TechMind Infosys was born from a simple yet powerful idea: to
              build a bridge between exceptional tech talent and companies
              driving innovation. What started as a small team of passionate
              technologists has grown into a global force in tech staffing and
              solutions.
            </p>
          </motion.div>

          {/* Timeline */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative"
          >
            {/* Vertical Line */}
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-blue-200"></div>
            <div className="absolute left-8 top-0 h-3/4 w-0.5 bg-blue-600"></div>

            {/* Timeline Items */}
            <div className="space-y-12">
              {timelineData.map((item, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  className="relative flex items-start"
                >
                  {/* Year Circle */}
                  <div className="flex-shrink-0 w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm z-10 shadow-lg">
                    {item.year}
                  </div>

                  {/* Content Card */}
                  <div className="ml-8 flex-1">
                    <div className="bg-gray-50 rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow duration-300">
                      <div className="flex items-start gap-4">
                        {/* Icon */}
                        <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <item.icon className="w-6 h-6 text-blue-600" />
                        </div>

                        {/* Content */}
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-gray-900 mb-3">
                            {item.title}
                          </h3>
                          <p className="text-gray-600 leading-relaxed mb-4">
                            {item.description}
                          </p>
                          <button className="inline-flex items-center text-blue-600 hover:text-blue-700 font-medium transition-colors duration-200">
                            {item.link}
                            <ArrowRight className="ml-1 w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      {/* Values Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            {/* <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide everything we do, from how we develop our
              products to how we serve our clients and nurture our team.
            </p> */}
            <p class="text-lg text-gray-300 max-w-3xl mx-auto">These principles guide everything we do, from how we develop our products to how we serve our clients and nurture our team.</p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {values.map((value, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white p-8 rounded-xl shadow-lg text-center"
              >
                <div className="w-16 h-16 bg-[#F3F4F6] rounded-full flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-black" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

     

      {/* Team Section */}
      {/* <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Leadership Team
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Experienced professionals dedicated to your success
            </p>
          </motion.div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {team.map((member, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="bg-white rounded-xl shadow-lg overflow-hidden"
              >
                <div className="h-48 bg-gray-200 flex items-center justify-center">
                  <Users className="w-16 h-16 text-gray-400" />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    {member.name}
                  </h3>
                  <p className="text-blue-600 font-medium mb-3">
                    {member.role}
                  </p>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      <section className="py-20 bg-[#1A193B] text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 lg:p-12 border border-gray-700/50 shadow-2xl"
          >
            {/* Quote Icon */}
            {/* <div className="flex justify-center mb-8">
              <div className="text-6xl text-gray-400 font-serif leading-none">
                "
              </div>
            </div> */}

            <div className="flex flex-col lg:flex-row items-center lg:items-start gap-8">
              {/* Profile Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="flex-shrink-0"
              >
                <div className="w-24 h-24 lg:w-32 lg:h-32 rounded-full overflow-hidden border-4 border-gray-600 shadow-lg">
                  <img
                    src={ceoImage}
                    alt="CEO Profile"
                    className="w-full h-full object-cover"
                  />
                </div>
              </motion.div>

              {/* Quote Content */}
              <div className="flex-1 text-center lg:text-left">
                <motion.blockquote
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="text-xl lg:text-2xl font-medium text-white mb-6 leading-relaxed"
                >
                  Our commitment is to deliver exceptional value through
                  technology and talent.
                </motion.blockquote>

                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="text-gray-300 text-lg mb-6 leading-relaxed"
                >
                  We believe that the right people, with the right tools, can
                  transform businesses and drive meaningful change in today's
                  digital world.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                  className="mb-8"
                >
                  <cite className="text-gray-400 text-sm not-italic">
                    John Doe, CEO of TechMind Infosys
                  </cite>
                </motion.div>

                {/* Connect Button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 1.0 }}
                >
                  <Link to="/contact">
                    <button className="inline-flex items-center px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-1">
                      Connect with us
                    </button>
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Client Success Stories
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
              We've helped companies across industries achieve remarkable
              results through our technology solutions and talented
              professionals.
            </p>
          </motion.div>

          {/* Success Stories Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {successStories.map((story, index) => (
              <motion.div
                key={index}
                variants={containerVariants}
                className="group bg-white rounded-xl p-8 shadow-sm border border-gray-200 hover:shadow-lg hover:border-blue-200 transition-all duration-300"
              >
                {/* Icon */}
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-50 transition-colors duration-300">
                  <story.icon className="w-8 h-8 text-gray-600 group-hover:text-blue-600 transition-colors duration-300" />
                </div>

                {/* Content */}
                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-900 leading-tight">
                    {story.title}
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    {story.description}
                  </p>

                  <div className="pt-4 border-t border-gray-100">
                    <p className="text-sm font-medium text-blue-600">
                      <span className="text-gray-500">Results: </span>
                      {story.results}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Call to Action */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <button className="inline-flex items-center px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg">
              View More Case Studies
            </button>
          </motion.div> */}
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-20 bg-[#1A193B] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl lg:text-4xl font-bold mb-4">
              Let's Build Something Amazing Together
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
              Whether you're looking for exceptional tech talent or innovative
              solutions, TechMind is ready to help you achieve your goals.
            </p>
            <Link to={"/services"} className="block">
              <button className="inline-flex items-center px-8 py-4 bg-white text-blue-600 hover:bg-gray-100 rounded-lg font-semibold transition-colors duration-200">
                {/* Get Started Today */}
                Start A Project
                {/* Contact Us */}
                <ArrowRight className="ml-2 w-5 h-5" />
              </button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-[#1A193B] text-white">
        <CommonContactUs />
      </section>
    </div>
  );
};

export default AboutPage;
